/**
 * API Service para 501 Dart League
 * Conecta con el backend de Railway
 */

const DEFAULT_BACKEND_ORIGIN = 'https://501backend-production.up.railway.app';

const normalizeUrl = (url) => (url || '').trim().replace(/\/+$/, '');

const configuredApiUrl = normalizeUrl(process.env.REACT_APP_API_URL);
const configuredBackendOrigin = normalizeUrl(process.env.REACT_APP_BACKEND_URL);

const API_URL = configuredApiUrl
  || `${configuredBackendOrigin || DEFAULT_BACKEND_ORIGIN}/api`;
const BACKEND_ORIGIN = normalizeUrl(configuredBackendOrigin || DEFAULT_BACKEND_ORIGIN);

export const DARTLEAGUE_API_URL = API_URL;

// CachÃƒÂ© simple con TTL para evitar peticiones duplicadas
const cache = new Map();
const CACHE_TTL = 60000; // 60 segundos
const STATS_CACHE_TTL = 300000; // 5 minutos - para estadÃƒÂ­sticas pesadas que no cambian durante entrenamientos

class DartLeagueAPI {
  constructor() {
    this.token = localStorage.getItem('dartleague_token');
    this.user = JSON.parse(localStorage.getItem('dartleague_user') || 'null');
    this.authFailureListeners = new Set();
    this.refreshPromise = null;
  }

  syncSessionFromStorage() {
    this.token = localStorage.getItem('dartleague_token');
    try {
      this.user = JSON.parse(localStorage.getItem('dartleague_user') || 'null');
    } catch {
      this.user = null;
    }
  }

  onAuthFailure(callback) {
    this.authFailureListeners.add(callback);
    return () => {
      this.authFailureListeners.delete(callback);
    };
  }

  notifyAuthFailure(reason) {
    this.authFailureListeners.forEach((callback) => {
      try {
        callback({ reason });
      } catch (error) {
        console.error('Error notificando expiraciÃƒÂ³n de sesiÃƒÂ³n:', error);
      }
    });
  }

  parseJwtPayload(token) {
    try {
      if (!token || typeof atob !== 'function') return null;
      const parts = token.split('.');
      if (parts.length < 2) return null;
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = `${base64}${'='.repeat((4 - (base64.length % 4)) % 4)}`;
      return JSON.parse(atob(padded));
    } catch {
      return null;
    }
  }

  isTokenExpired(token = this.token, clockSkewMs = 30000) {
    const payload = this.parseJwtPayload(token);
    if (!payload?.exp) return false;
    return (payload.exp * 1000) <= (Date.now() + clockSkewMs);
  }

  setSession(token, user) {
    this.token = token || null;
    this.user = user || null;

    if (this.token) {
      localStorage.setItem('dartleague_token', this.token);
    } else {
      localStorage.removeItem('dartleague_token');
    }

    if (this.user) {
      localStorage.setItem('dartleague_user', JSON.stringify(this.user));
    } else {
      localStorage.removeItem('dartleague_user');
    }
  }

  hasSessionState() {
    this.syncSessionFromStorage();
    return Boolean(this.token && this.user);
  }

  createMockResponse(status, data = {}) {
    return {
      ok: status >= 200 && status < 300,
      status,
      async json() {
        return data;
      },
    };
  }

  isBackendRequest(input) {
    const url = typeof input === 'string' ? input : input?.url || '';
    return url.startsWith(API_URL) || url.startsWith(BACKEND_ORIGIN);
  }

  isPublicAuthEndpoint(input) {
    const url = typeof input === 'string' ? input : input?.url || '';
    return [
      `${API_URL}/auth/login`,
      `${API_URL}/auth/register`,
      `${API_URL}/auth/refresh`,
      `${API_URL}/auth/logout`,
      `${API_URL}/auth/search-users`,
      `${API_URL}/auth/jugadores`,
    ].some((publicUrl) => url.startsWith(publicUrl));
  }

  async ensureValidAccessToken() {
    this.syncSessionFromStorage();

    if (!this.token || !this.user) {
      return false;
    }

    if (!this.isTokenExpired(this.token)) {
      return true;
    }

    return this.refreshAccessToken();
  }

  async bootstrapSession() {
    if (!this.hasSessionState()) {
      return false;
    }

    return this.ensureValidAccessToken();
  }

  async refreshAccessToken() {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    if (!this.hasSessionState()) {
      return false;
    }

    this.refreshPromise = (async () => {
      try {
        const response = await this.authFetch(`${API_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });

        if (!response.ok) {
          this.handleAuthFailure('REFRESH_FAILED');
          return false;
        }

        const data = await response.json();
        this.setSession(data.token, data.user || this.user);
        return true;
      } catch (error) {
        console.error('Error refrescando access token:', error);
        this.handleAuthFailure('REFRESH_FAILED');
        return false;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  async authFetch(input, init = {}) {
    const { retryOnUnauthorized = true, requiresAuth, ...fetchOptions } = init;
    const backendRequest = this.isBackendRequest(input);
    const shouldRequireAuth = typeof requiresAuth === 'boolean'
      ? requiresAuth
      : backendRequest && !this.isPublicAuthEndpoint(input);

    if (shouldRequireAuth) {
      const hasValidAccessToken = await this.ensureValidAccessToken();
      if (!hasValidAccessToken) {
        return this.createMockResponse(401, { message: 'SesiÃƒÂ³n no vÃƒÂ¡lida' });
      }
    }

    const headers = {
      ...(fetchOptions.headers || {}),
    };

    const hasBody = typeof fetchOptions.body !== 'undefined' && fetchOptions.body !== null;
    const isFormData = typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData;

    if (hasBody && !isFormData && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    if (shouldRequireAuth && this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const runRequest = () => fetch(input, {
      ...fetchOptions,
      headers,
      credentials: backendRequest ? 'include' : fetchOptions.credentials,
    });

    let response = await runRequest();

    if (response.status === 401 && shouldRequireAuth && retryOnUnauthorized) {
      const refreshed = await this.refreshAccessToken();

      if (refreshed) {
        this.syncSessionFromStorage();
        headers.Authorization = `Bearer ${this.token}`;
        response = await runRequest();
      }
    }

    if (response.status === 401 && shouldRequireAuth) {
      this.handleAuthFailure('REQUEST_UNAUTHORIZED');
    }

    return response;
  }

  clearSessionData() {
    this.token = null;
    this.user = null;
    localStorage.removeItem('dartleague_token');
    localStorage.removeItem('dartleague_user');
    localStorage.removeItem('dartleague-current-view');
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('dartleague-game-config');
      sessionStorage.removeItem('playerHomeStats');
    }
    this.invalidateCache();
  }

  handleAuthFailure(reason = 'SESSION_EXPIRED') {
    this.syncSessionFromStorage();
    const hadSession = Boolean(this.token && this.user);
    this.clearSessionData();
    if (hadSession) {
      this.notifyAuthFailure(reason);
    }
  }

  hasValidLocalSession() {
    return this.hasSessionState();
  }

  // ========== SISTEMA DE CACHE ==========
  
  /**
  * Obtiene datos del cache si existen y no han expirado
   * @param {string} key - Clave del cachÃƒÂ©
  * @returns {any|null} - Datos cacheados o null
   */
  getCached(key, customTTL = null) {
    const cached = cache.get(key);
    const ttl = customTTL || CACHE_TTL;
    if (cached && Date.now() - cached.timestamp < ttl) {
      console.log(`[API cache] Hit: ${key}`);
      return cached.data;
    }
    return null;
  }

  /**
  * Guarda datos en cache
   * @param {string} key - Clave del cachÃƒÂ©
   * @param {any} data - Datos a cachear
   */
  setCache(key, data) {
    cache.set(key, { data, timestamp: Date.now() });
  }

  /**
  * Invalida una entrada del cache
  * @param {string} key - Clave a invalidar (puede ser parcial para invalidar multiples)
   */
  invalidateCache(key) {
    if (key) {
      // Invalidar claves que coincidan parcialmente
      for (const k of cache.keys()) {
        if (k.includes(key)) {
          cache.delete(k);
        }
      }
    } else {
      // Invalidar todo el cache
      cache.clear();
    }
  }

  // Headers con autenticacion
  getHeaders() {
    this.syncSessionFromStorage();
    return {
      'Content-Type': 'application/json',
      ...(this.token && { 'Authorization': `Bearer ${this.token}` })
    };
  }

  // Login
  async login(username, password) {
    try {
      const response = await this.authFetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
        requiresAuth: false,
        retryOnUnauthorized: false,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error en el login');
      }

      const data = await response.json();

      // Guardar token y usuario (permitir tanto jugadores como bares)
      this.setSession(data.token, data.user);

      return data;
    } catch (error) {
      console.error('Error en login:', error);
      throw error;
    }
  }

  async register({ username, nombre, password, confirmPassword, tipoUsuario }) {
    try {
      const response = await this.authFetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, nombre, password, confirmPassword, tipoUsuario }),
        requiresAuth: false,
        retryOnUnauthorized: false,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.msg || error.message || 'Error en el registro');
      }

      const data = await response.json();
      this.setSession(data.token, data.user);
      return data;
    } catch (error) {
      console.error('Error en register:', error);
      throw error;
    }
  }

  // Logout
  logout() {
    void this.authFetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      requiresAuth: false,
      retryOnUnauthorized: false,
    });
    this.clearSessionData();
  }

  // Verificar si estÃƒÂ¡ autenticado
  isAuthenticated() {
    return this.hasSessionState();
  }

  // Obtener usuario actual
  getCurrentUser() {
    this.syncSessionFromStorage();
    return this.user;
  }

  // Obtener ligas del usuario (con cachÃƒÂ©)
  async getMyLeagues() {
    const cacheKey = `myLeagues_${this.user?._id}`;
    const cached = this.getCached(cacheKey, STATS_CACHE_TTL);
    if (cached) return cached;

    try {
      const response = await this.authFetch(`${API_URL}/ligas`, {
        headers: this.getHeaders()
      });

      if (!response.ok) throw new Error('Error obteniendo ligas');

      const ligas = await response.json();
      let result;
      
      // El recaudador es organizador de todas las ligas; el bar solo ve las suyas
      if (this.user.tipoUsuario === 'recaudador') {
        result = ligas;
      } else if (this.user.tipoUsuario === 'bar') {
        result = ligas.filter(liga => {
          // Comparar con el ID del bar (puede ser objeto o string)
          const barId = liga.bar?._id || liga.bar;
          return barId === this.user._id || barId === this.user.id;
        });
      } else {
        // Si es jugador, filtrar por participantes
        result = ligas.filter(liga => 
          liga.participantes.some(p => p._id === this.user._id)
        );
      }
      
      this.setCache(cacheKey, result);
      return result;
    } catch (error) {
      console.error('Error obteniendo ligas:', error);
      throw error;
    }
  }

  async getRecaudacionBares() {
    const response = await this.authFetch(`${API_URL}/recaudacion/bares`, {
      headers: this.getHeaders()
    });
    if (!response.ok) throw new Error('Error obteniendo bares');
    return response.json();
  }

  async getLigasControlRecaudacion(barId) {
    const response = await this.authFetch(`${API_URL}/recaudacion/bares/${barId}/ligas`, {
      headers: this.getHeaders()
    });
    if (!response.ok) throw new Error('Error obteniendo ligas');
    return response.json();
  }

  async guardarVisitaRecaudacion(ligaId, partidasPagadas) {
    const response = await this.authFetch(`${API_URL}/recaudacion/ligas/${ligaId}/visitas`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ partidasPagadas })
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.message || 'Error guardando la visita');
    return result;
  }

  // Obtener clasificaciÃƒÂ³n de una liga (con cachÃƒÂ©)
  async getClasificacion(ligaId) {
    const cacheKey = `clasificacion_${ligaId}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const response = await this.authFetch(`${API_URL}/ligas/${ligaId}/clasificacion`, {
        headers: this.getHeaders()
      });

      if (!response.ok) throw new Error('Error obteniendo clasificaciÃƒÂ³n');

      const result = await response.json();
      this.setCache(cacheKey, result);
      return result;
    } catch (error) {
      console.error('Error obteniendo clasificaciÃƒÂ³n:', error);
      throw error;
    }
  }

  // Obtener jornadas de una liga (con cachÃƒÂ©)
  async getJornadas(ligaId) {
    const cacheKey = `jornadas_${ligaId}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const response = await this.authFetch(`${API_URL}/ligas/${ligaId}/jornadas`, {
        headers: this.getHeaders()
      });

      if (!response.ok) throw new Error('Error obteniendo jornadas');

      const result = await response.json();
      this.setCache(cacheKey, result);
      return result;
    } catch (error) {
      console.error('Error obteniendo jornadas:', error);
      throw error;
    }
  }

  // Obtener estadÃƒÂ­sticas del usuario
  async getMyStats(ligaId = null) {
    try {
      const url = ligaId 
        ? `${API_URL}/estadisticas/usuario/${this.user._id}?ligaId=${ligaId}`
        : `${API_URL}/estadisticas/usuario/${this.user._id}`;

      const response = await this.authFetch(url, {
        headers: this.getHeaders()
      });

      if (!response.ok) throw new Error('Error obteniendo estadÃƒÂ­sticas');

      return await response.json();
    } catch (error) {
      console.error('Error obteniendo estadÃƒÂ­sticas:', error);
      throw error;
    }
  }

  // Obtener promedio total y porcentaje de victorias desde el backend (con cachÃƒÂ©)
  async getPromedioTotal() {
    const cacheKey = `promedioTotal_${this.user?._id}`;
    const cached = this.getCached(cacheKey, STATS_CACHE_TTL);
    if (cached) return cached;

    try {
      // Usar el endpoint optimizado del backend (1 sola query a MongoDB)
      const response = await this.authFetch(`${API_URL}/estadisticas/promedio-total/${this.user._id}`, {
        headers: this.getHeaders()
      });

      if (!response.ok) throw new Error('Error obteniendo promedio total');

      const result = await response.json();
      this.setCache(cacheKey, result);
      return result;
    } catch (error) {
      console.error('Ã°Å¸â€™Â¥ Error obteniendo promedio total:', error);
      return {
        promedioTotal: 0,
        porcentajeVictorias: 0,
        totalPartidas: 0,
        totalVictorias: 0
      };
    }
  }

  // Obtener estadÃƒÂ­sticas online del usuario (con cachÃƒÂ©)
  async getOnlineStats() {
    if (!this.hasValidLocalSession() || !this.user?._id) return null;
    const cacheKey = `onlineStats_${this.user._id}`;
    const cached = this.getCached(cacheKey, STATS_CACHE_TTL);
    if (cached) return cached;

    try {
      const response = await this.authFetch(`${API_URL}/online/stats/${this.user._id}`, {
        headers: this.getHeaders()
      });

      if (response.status === 401) {
        this.handleAuthFailure('ONLINE_STATS_UNAUTHORIZED');
        return null;
      }

      if (!response.ok) return null;

      const result = await response.json();
      this.setCache(cacheKey, result);
      return result;
    } catch (error) {
      console.error('Error obteniendo stats online:', error);
      return null;
    }
  }

  async getOnlineMetrics(days = 30) {
    if (!this.hasValidLocalSession() || this.user?.username?.toLowerCase() !== 'juanmi') {
      return null;
    }

    try {
      const response = await this.authFetch(`${API_URL}/online/metrics?days=${days}`, {
        headers: this.getHeaders()
      });

      if (response.status === 401) {
        this.handleAuthFailure('ONLINE_METRICS_UNAUTHORIZED');
        return null;
      }

      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error('Error obteniendo métricas online:', error);
      return null;
    }
  }

  // Obtener historial de partidas online con paginaciÃƒÂ³n
  async getOnlineMatchHistory(page = 1, limit = 20) {
    if (!this.hasValidLocalSession() || !this.user?._id) {
      return { matches: [], pagination: { page: 1, total: 0, totalPages: 0 } };
    }

    try {
      const response = await this.authFetch(
        `${API_URL}/online/matches/${this.user._id}?page=${page}&limit=${limit}`,
        { headers: this.getHeaders() }
      );

      if (response.status === 401) {
        this.handleAuthFailure('ONLINE_HISTORY_UNAUTHORIZED');
        return { matches: [], pagination: { page: 1, total: 0, totalPages: 0 } };
      }

      if (!response.ok) return { matches: [], pagination: { page: 1, total: 0, totalPages: 0 } };

      return await response.json();
    } catch (error) {
      console.error('Error obteniendo historial online:', error);
      return { matches: [], pagination: { page: 1, total: 0, totalPages: 0 } };
    }
  }

  // Obtener tendencia diaria de media online (ÃƒÂºltimos 3 meses, sin filtros)
  async getOnlineDailyTrend() {
    if (!this.hasValidLocalSession() || !this.user?._id) return { days: [], period: 'last_3_months' };

    try {
      const response = await this.authFetch(
        `${API_URL}/online/trend/${this.user._id}`,
        { headers: this.getHeaders() }
      );

      if (response.status === 401) {
        this.handleAuthFailure('ONLINE_TREND_UNAUTHORIZED');
        return { days: [], period: 'last_3_months' };
      }

      if (!response.ok) return { days: [], period: 'last_3_months' };

      return await response.json();
    } catch (error) {
      console.error('Error obteniendo tendencia online:', error);
      return { days: [], period: 'last_3_months' };
    }
  }

  // Obtener partidas del usuario
  async getMyMatches(ligaId = null) {
    try {
      const ligas = ligaId ? [ligaId] : (await this.getMyLeagues()).map(l => l._id);
      const allMatches = [];

      for (const id of ligas) {
        const jornadas = await this.getJornadas(id);
        
        jornadas.forEach(jornada => {
          jornada.partidas.forEach(partida => {
            // Solo partidas del usuario
            if (partida.jugador1._id === this.user._id || 
                partida.jugador2._id === this.user._id) {
              allMatches.push({
                ...partida,
                jornada: jornada.jornada,
                ligaNombre: jornadas.ligaNombre || 'Liga'
              });
            }
          });
        });
      }

      return allMatches;
    } catch (error) {
      console.error('Error obteniendo partidas:', error);
      throw error;
    }
  }

  // Actualizar resultado de una partida (solo para usuarios bar)
  async updatePartidaResult(ligaId, jornadaNumero, partidaId, resultado) {
    try {
      if (!['bar', 'recaudador'].includes(this.user.tipoUsuario)) {
        throw new Error('Solo los bares pueden editar resultados');
      }

      // Usar sets directamente (no convertir desde puntos)
      const sets1 = resultado.setsJugador1 || 0;
      const sets2 = resultado.setsJugador2 || 0;

      // Primera llamada: actualizar resultado y ganador
      const responseEditar = await this.authFetch(`${API_URL}/ligas/partidas/${partidaId}/editar`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify({
          ganadorId: resultado.ganadorId,
          sets: [sets1, sets2]
        })
      });

      if (!responseEditar.ok) {
        const error = await responseEditar.json();
        throw new Error(error.message || 'Error actualizando resultado');
      }

      // Segunda llamada: actualizar los 180s
      try {
        await this.authFetch(`${API_URL}/ligas/partidas/${partidaId}`, {
          method: 'PATCH',
          headers: this.getHeaders(),
          body: JSON.stringify({
            ciento80sJugador1: resultado.ciento80sJugador1 || 0,
            ciento80sJugador2: resultado.ciento80sJugador2 || 0
          })
        });
      } catch (error) {
        console.warn('No se pudieron actualizar los 180s:', error);
      }

      // Tercera llamada: actualizar estado de pago
      const responsePago = await this.authFetch(`${API_URL}/ligas/partidas/${partidaId}/pago`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify({
          pagada: resultado.pagado
        })
      });

      if (!responsePago.ok) {
        const error = await responsePago.json();
        throw new Error(error.message || 'Error actualizando estado de pago');
      }

      return await responsePago.json();
    } catch (error) {
      console.error('Error actualizando partida:', error);
      throw error;
    }
  }

  async resetPartida(partidaId) {
    try {
      if (!['bar', 'recaudador'].includes(this.user.tipoUsuario)) {
        throw new Error('Solo los bares pueden resetear partidas');
      }

      const response = await this.authFetch(`${API_URL}/ligas/partidas/${partidaId}/resetear`, {
        method: 'PUT',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error reseteando partida');
      }

      return await response.json();
    } catch (error) {
      console.error('Error reseteando partida:', error);
      throw error;
    }
  }

  // Actualizar solo el estado de pago de una partida
  async togglePagoPartida(partidaId, pagada) {
    try {
      if (!['bar', 'recaudador'].includes(this.user.tipoUsuario)) {
        throw new Error('Solo los bares pueden actualizar el estado de pago');
      }

      const response = await this.authFetch(`${API_URL}/ligas/partidas/${partidaId}/pago`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify({ pagada })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error actualizando estado de pago');
      }

      return await response.json();
    } catch (error) {
      console.error('Error actualizando pago:', error);
      throw error;
    }
  }

  // ==================== CAMPEONATOS ====================

  // Verificar si el usuario participa en un campeonato
  _verificarParticipacion(campeonato, userId) {
    // Verificar si es admin
    let adminId = '';
    if (campeonato.administrador) {
      if (typeof campeonato.administrador === 'string') {
        adminId = campeonato.administrador;
      } else if (campeonato.administrador._id) {
        adminId = String(campeonato.administrador._id);
      } else if (campeonato.administrador.$oid) {
        adminId = campeonato.administrador.$oid;
      } else {
        adminId = String(campeonato.administrador);
      }
    }
    adminId = adminId.toLowerCase();
    
    if (adminId === userId) {
      return { participa: true, esAdmin: true };
    }
    
    // Verificar si es participante
    const esParticipante = campeonato.participantes?.some(p => {
      let participanteId = '';
      if (p.usuario) {
        if (typeof p.usuario === 'string') {
          participanteId = p.usuario;
        } else if (p.usuario._id) {
          participanteId = String(p.usuario._id);
        } else {
          participanteId = String(p.usuario);
        }
      }
      participanteId = participanteId.toLowerCase();
      return participanteId === userId;
    });
    
    return { participa: esParticipante, esAdmin: false };
  }

  // Obtener TODOS los campeonatos (para que cualquiera pueda ver)
  // Marca cuÃƒÂ¡les son del usuario y los ordena primero (con cachÃƒÂ©)
  async getAllCampeonatos() {
    const cacheKey = `allCampeonatos_${this.user?._id}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const response = await this.authFetch(`${API_URL}/campeonatos`, {
        headers: this.getHeaders()
      });

      if (!response.ok) throw new Error('Error obteniendo campeonatos');

      const data = await response.json();
      const campeonatos = data.campeonatos || data || [];
      
      // Obtener el ID del usuario actual normalizado
      const userId = String(this.user._id || this.user.id || '').toLowerCase();
      
      // Marcar cada campeonato con info de participaciÃƒÂ³n
      const campeonatosConInfo = campeonatos.map(c => {
        const { participa, esAdmin } = this._verificarParticipacion(c, userId);
        return {
          ...c,
          _usuarioParticipa: participa,
          _usuarioEsAdmin: esAdmin
        };
      });
      
      // Ordenar: primero los que participa, luego los demÃƒÂ¡s (ordenados por fecha)
      campeonatosConInfo.sort((a, b) => {
        // Primero por participaciÃƒÂ³n
        if (a._usuarioParticipa && !b._usuarioParticipa) return -1;
        if (!a._usuarioParticipa && b._usuarioParticipa) return 1;
        // Luego por fecha (mÃƒÂ¡s recientes primero)
        return new Date(b.fechaInicio || b.createdAt) - new Date(a.fechaInicio || a.createdAt);
      });
      
      this.setCache(cacheKey, campeonatosConInfo);
      return campeonatosConInfo;
    } catch (error) {
      console.error('Error obteniendo campeonatos:', error);
      throw error;
    }
  }

    async crearCampeonato(campeonatoData) {
      try {
        const response = await this.authFetch(`${API_URL}/campeonatos`, {
          method: 'POST',
          headers: this.getHeaders(),
          body: JSON.stringify(campeonatoData),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Error creando torneo');
        }

        const result = await response.json();
        this.invalidateCache('allCampeonatos_');
        return result;
      } catch (error) {
        console.error('Error creando campeonato:', error);
        throw error;
      }
    }

    async generarBracketCampeonato(campeonatoId) {
      try {
        const response = await this.authFetch(`${API_URL}/campeonatos/${campeonatoId}/generar-bracket`, {
          method: 'POST',
          headers: this.getHeaders(),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Error generando bracket');
        }

        const result = await response.json();
        this.invalidateCache('allCampeonatos_');
        return result;
      } catch (error) {
        console.error('Error generando bracket de campeonato:', error);
        throw error;
      }
    }

  // Obtener campeonatos del usuario (mantener para compatibilidad)
  async getMyCampeonatos() {
    try {
      const todos = await this.getAllCampeonatos();
      // Filtrar solo los que participa
      return todos.filter(c => c._usuarioParticipa);
    } catch (error) {
      console.error('Error obteniendo campeonatos:', error);
      throw error;
    }
  }

  // Obtener detalle de un campeonato
  async getCampeonato(campeonatoId) {
    try {
      const response = await this.authFetch(`${API_URL}/campeonatos/${campeonatoId}`, {
        headers: this.getHeaders()
      });

      if (!response.ok) throw new Error('Error obteniendo campeonato');

      return await response.json();
    } catch (error) {
      console.error('Error obteniendo campeonato:', error);
      throw error;
    }
  }

  // Obtener bracket de un campeonato
  async getBracket(campeonatoId) {
    try {
      const response = await this.authFetch(`${API_URL}/campeonatos/${campeonatoId}/bracket`, {
        headers: this.getHeaders()
      });

      if (!response.ok) throw new Error('Error obteniendo bracket');

      return await response.json();
    } catch (error) {
      console.error('Error obteniendo bracket:', error);
      throw error;
    }
  }

  async getPartidaDetalle(partidaId) {
    try {
      const response = await this.authFetch(`${API_URL}/partidas/${partidaId}`, {
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Error obteniendo detalle de partida');
      }

      return await response.json();
    } catch (error) {
      console.error('Error obteniendo detalle de partida:', error);
      throw error;
    }
  }

  // Obtener ranking de un campeonato
  async getRanking(campeonatoId) {
    try {
      const response = await this.authFetch(`${API_URL}/campeonatos/${campeonatoId}/ranking`, {
        headers: this.getHeaders()
      });

      if (!response.ok) throw new Error('Error obteniendo ranking');

      const data = await response.json();
      return data.ranking || [];
    } catch (error) {
      console.error('Error obteniendo ranking:', error);
      throw error;
    }
  }

  // ==================== ADMINISTRACIÃƒâ€œN DE BRACKETS (SOLO BARES) ====================

  /**
   * Editar resultado de una partida ya completada
   */
  async editarResultadoPartida(campeonatoId, partidaId, datos) {
    try {
      if (this.user.tipoUsuario !== 'bar') {
        throw new Error('Solo los bares pueden editar resultados');
      }

      const response = await this.authFetch(`${API_URL}/campeonatos/${campeonatoId}/partida/${partidaId}/editar-resultado`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(datos)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error editando resultado');
      }

      return await response.json();
    } catch (error) {
      console.error('Error editando resultado:', error);
      throw error;
    }
  }

  /**
   * Resetear una partida completada (deshacer resultado)
   */
  async resetearPartidaCampeonato(campeonatoId, partidaId) {
    try {
      if (this.user.tipoUsuario !== 'bar') {
        throw new Error('Solo los bares pueden resetear partidas');
      }

      const response = await this.authFetch(`${API_URL}/campeonatos/${campeonatoId}/partida/${partidaId}/resetear`, {
        method: 'PUT',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error reseteando partida');
      }

      return await response.json();
    } catch (error) {
      console.error('Error reseteando partida:', error);
      throw error;
    }
  }

  /**
   * Asignar ganador manualmente sin jugar
   */
  async asignarGanadorPartida(campeonatoId, partidaId, datos) {
    try {
      if (this.user.tipoUsuario !== 'bar') {
        throw new Error('Solo los bares pueden asignar ganadores');
      }

      const response = await this.authFetch(`${API_URL}/campeonatos/${campeonatoId}/partida/${partidaId}/asignar-ganador`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(datos)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error asignando ganador');
      }

      return await response.json();
    } catch (error) {
      console.error('Error asignando ganador:', error);
      throw error;
    }
  }

  /**
   * Registrar resultado de partida con avance automÃƒÂ¡tico de bracket
   * Usa la misma lÃƒÂ³gica que la app mÃƒÂ³vil
   */
  async registrarResultadoPartida(campeonatoId, partidaId, ganadorId, sets1, sets2) {
    try {
      if (this.user.tipoUsuario !== 'bar') {
        throw new Error('Solo los bares pueden registrar resultados');
      }

      const response = await this.authFetch(`${API_URL}/campeonatos/${campeonatoId}/partida/${partidaId}/resultado`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          ganadorId,
          resultados: {
            jugador1Sets: parseInt(sets1) || 0,
            jugador2Sets: parseInt(sets2) || 0,
          }
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error registrando resultado');
      }

      return await response.json();
    } catch (error) {
      console.error('Error registrando resultado:', error);
      throw error;
    }
  }

  /**
   * Cambiar jugador en una partida
   */
  async cambiarJugadorPartida(campeonatoId, partidaId, datos) {
    try {
      if (this.user.tipoUsuario !== 'bar') {
        throw new Error('Solo los bares pueden cambiar jugadores');
      }

      const response = await this.authFetch(`${API_URL}/campeonatos/${campeonatoId}/partida/${partidaId}/cambiar-jugador`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(datos)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error cambiando jugador');
      }

      return await response.json();
    } catch (error) {
      console.error('Error cambiando jugador:', error);
      throw error;
    }
  }

  /**
   * Obtener lista de jugadores disponibles para el campeonato
   */
  async getJugadoresDisponibles(campeonatoId) {
    try {
      const response = await this.authFetch(`${API_URL}/campeonatos/${campeonatoId}/jugadores-disponibles`, {
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error obteniendo jugadores');
      }

      return await response.json();
    } catch (error) {
      console.error('Error obteniendo jugadores disponibles:', error);
      throw error;
    }
  }

  /**
   * Regenerar todo el bracket (PELIGROSO)
   */
  async regenerarBracket(campeonatoId, confirmar = false) {
    try {
      if (this.user.tipoUsuario !== 'bar') {
        throw new Error('Solo los bares pueden regenerar brackets');
      }

      const response = await this.authFetch(`${API_URL}/campeonatos/${campeonatoId}/regenerar-bracket`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify({ confirmar })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error regenerando bracket');
      }

      return await response.json();
    } catch (error) {
      console.error('Error regenerando bracket:', error);
      throw error;
    }
  }

  /**
   * Actualizar configuraciÃƒÂ³n del campeonato (juegos para ganar)
   */
  async actualizarConfiguracion(campeonatoId, configuracion) {
    try {
      if (this.user.tipoUsuario !== 'bar') {
        throw new Error('Solo los bares pueden modificar la configuraciÃƒÂ³n');
      }

      const response = await this.authFetch(`${API_URL}/campeonatos/${campeonatoId}/configuracion`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(configuracion)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error actualizando configuraciÃƒÂ³n');
      }

      return await response.json();
    } catch (error) {
      console.error('Error actualizando configuraciÃƒÂ³n:', error);
      throw error;
    }
  }

  // ==================== LIGAS - CREACIÃƒâ€œN ====================

  /**
   * Buscar usuarios por username
   */
  async searchUsers(query, tipo = null) {
    try {
      const queryParams = new URLSearchParams({ q: query });
      if (tipo) {
        queryParams.set('tipo', tipo);
      }

      const response = await this.authFetch(`${API_URL}/auth/search-users?${queryParams.toString()}`, {
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error buscando usuarios');
      }

      return await response.json();
    } catch (error) {
      console.error('Error buscando usuarios:', error);
      throw error;
    }
  }

  /**
   * Crear una nueva liga
   */
  async crearLiga(ligaData) {
    try {
      if (this.user.tipoUsuario !== 'bar') {
        throw new Error('Solo los bares pueden crear ligas');
      }

      const response = await this.authFetch(`${API_URL}/ligas`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(ligaData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error creando liga');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creando liga:', error);
      throw error;
    }
  }

  /**
   * Generar calendario de una liga
   */
  async generarCalendario(ligaId) {
    try {
      const response = await this.authFetch(`${API_URL}/ligas/${ligaId}/generar-calendario`, {
        method: 'POST',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error generando calendario');
      }

      return await response.json();
    } catch (error) {
      console.error('Error generando calendario:', error);
      throw error;
    }
  }

  /**
   * Activar una liga
   */
  async activarLiga(ligaId) {
    try {
      const response = await this.authFetch(`${API_URL}/ligas/${ligaId}/activar`, {
        method: 'POST',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error activando liga');
      }

      return await response.json();
    } catch (error) {
      console.error('Error activando liga:', error);
      throw error;
    }
  }

  /**
   * Validar configuraciÃƒÂ³n de liga
   */
  async validarLiga(numParticipantes) {
    try {
      const response = await this.authFetch(`${API_URL}/ligas/validar/${numParticipantes}`, {
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error validando liga');
      }

      return await response.json();
    } catch (error) {
      console.error('Error validando liga:', error);
      throw error;
    }
  }

  // ==================== ADMIN - GESTIÃƒâ€œN DE USUARIOS ====================

  /**
   * Buscar usuario por username (admin)
   */
  async adminGetUser(username) {
    try {
      const response = await this.authFetch(`${API_URL}/auth/admin/user/${encodeURIComponent(username)}`, {
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Usuario no encontrado');
      }

      return await response.json();
    } catch (error) {
      console.error('Error buscando usuario:', error);
      throw error;
    }
  }

  /**
   * Cambiar contraseÃƒÂ±a de usuario (admin)
   */
  async adminChangePassword(userId, newPassword) {
    try {
      const response = await this.authFetch(`${API_URL}/auth/admin/change-password`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify({ userId, newPassword })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error cambiando contraseÃƒÂ±a');
      }

      return await response.json();
    } catch (error) {
      console.error('Error cambiando contraseÃƒÂ±a:', error);
      throw error;
    }
  }

  // ========== CONTACTO / VIDEOLLAMADA ==========

  // Obtener datos del usuario autenticado (incluye contacto)
  async getMe() {
    try {
      if (!this.hasValidLocalSession()) return null;

      const response = await this.authFetch(`${API_URL}/auth/me`, {
        headers: this.getHeaders()
      });

      if (response.status === 401) {
        this.handleAuthFailure('AUTH_ME_UNAUTHORIZED');
        return null;
      }

      if (!response.ok) return null;
      const me = await response.json();

      if (me) {
        this.user = {
          ...(this.user || {}),
          ...me
        };
        localStorage.setItem('dartleague_user', JSON.stringify(this.user));
      }

      return me;
    } catch (error) {
      console.error('Error obteniendo perfil:', error);
      return null;
    }
  }

  // Actualizar plataforma de contacto
  async updateContacto(plataforma, valor) {
    try {
      const response = await this.authFetch(`${API_URL}/auth/contacto`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify({ plataforma, valor })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error actualizando contacto');
      }

      const result = await response.json();
      
      // Actualizar el usuario local con el nuevo contacto
      if (this.user) {
        this.user.contacto = result.contacto;
        localStorage.setItem('dartleague_user', JSON.stringify(this.user));
      }

      return result;
    } catch (error) {
      console.error('Error actualizando contacto:', error);
      throw error;
    }
  }

  // ========== ANÃƒÂLISIS DE MECÃƒÂNICA ==========

  // Comprobar si puede analizar (cooldown 30 dÃƒÂ­as)
  async getMechanicsStatus() {
    try {
      const response = await this.authFetch(`${API_URL}/mechanics/status`, {
        headers: this.getHeaders()
      });
      if (!response.ok) throw new Error('Error obteniendo estado');
      return await response.json();
    } catch (error) {
      console.error('Error en getMechanicsStatus:', error);
      throw error;
    }
  }

  // Enviar vÃƒÂ­deo para anÃƒÂ¡lisis
  async analyzeMechanics(videoBase64, mimeType = 'video/webm') {
    try {
      const response = await this.authFetch(`${API_URL}/mechanics/analyze`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ videoBase64, mimeType })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error en el anÃƒÂ¡lisis');
      }

      return await response.json();
    } catch (error) {
      console.error('Error en analyzeMechanics:', error);
      throw error;
    }
  }

  // Historial de anÃƒÂ¡lisis
  async getMechanicsHistory() {
    try {
      const response = await this.authFetch(`${API_URL}/mechanics/history`, {
        headers: this.getHeaders()
      });
      if (!response.ok) throw new Error('Error obteniendo historial');
      return await response.json();
    } catch (error) {
      console.error('Error en getMechanicsHistory:', error);
      throw error;
    }
  }

  // Enviar feedback sobre un anÃƒÂ¡lisis
  async sendMechanicsFeedback(analysisId, feedback) {
    try {
      const response = await this.authFetch(`${API_URL}/mechanics/${analysisId}/feedback`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ feedback })
      });
      if (!response.ok) throw new Error('Error enviando feedback');
      return await response.json();
    } catch (error) {
      console.error('Error en sendMechanicsFeedback:', error);
      throw error;
    }
  }

  // ========== TURN CREDENTIALS (WebRTC) ==========

  /**
   * Obtiene credenciales TURN (ICE servers) para WebRTC.
   * Si el backend no tiene TURN configurado, devuelve solo STUN (funciona igual que antes).
   * Se cachea 4 min en cliente para no pedir en cada reconexiÃƒÂ³n.
   */
  async getTurnCredentials() {
    const cacheKey = 'turn_credentials';
    const cached = this.getCached(cacheKey, 240000); // 4 min
    if (cached) return cached;

    try {
      if (!this.hasValidLocalSession()) {
        return {
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' },
          ],
          turnAvailable: false,
        };
      }

      const response = await this.authFetch(`${API_URL}/turn/credentials`, {
        headers: this.getHeaders(),
      });
      if (response.status === 401) {
        this.handleAuthFailure('TURN_UNAUTHORIZED');
        return {
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' },
          ],
          turnAvailable: false,
        };
      }
      if (!response.ok) throw new Error('Error obteniendo TURN credentials');
      const data = await response.json();
      this.setCache(cacheKey, data);
      return data;
    } catch (error) {
      console.warn('Ã¢Å¡Â Ã¯Â¸Â No se pudieron obtener TURN credentials, usando solo STUN:', error.message);
      // Fallback: devolver solo STUN (nunca romper el flujo)
      return {
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
        ],
        turnAvailable: false,
      };
    }
  }
}

// Exportar instancia ÃƒÂºnica
const dartLeagueAPIInstance = new DartLeagueAPI();
export default dartLeagueAPIInstance;
